import { v4 as uuidV4 } from "uuid";

import type {
  ConfidenceBucket,
  CustomerMinimal,
  Group,
  Suggestion,
} from "../state/types";
import {
  BUCKET_1_MIN_MARGIN,
  BUCKET_1_MIN_SCORE,
  BUCKET_2_MIN_MARGIN,
  BUCKET_2_MIN_SCORE,
} from "./config";
import { normalizeForMatch, stripLegalSuffixes } from "./tokenize";

export type ScoredFileInput = {
  id: string;
  suggestions: Suggestion[];
};

export type FileBucketAssignment = {
  id: string;
  initial_bucket: ConfidenceBucket;
};

export type BuildGroupsResult = {
  file_buckets: FileBucketAssignment[];
  groups: Group[];
  ungrouped_file_ids: string[];
};

/**
 * Build the set of customer ids whose normalized long-name token sequence
 * collides with at least one other customer. Any file whose top suggestion
 * points at one of these customers must go to bucket 3 per the spec's tie
 * rules.
 */
function buildAmbiguousCustomerIds(
  customers: CustomerMinimal[],
): Set<string> {
  const phraseToCustomerIds = new Map<string, string[]>();
  for (const customer of customers) {
    const tokens = stripLegalSuffixes(normalizeForMatch(customer.long_name));
    if (tokens.length === 0) continue;
    const phrase = tokens.join(" ");
    const existing = phraseToCustomerIds.get(phrase);
    if (existing) {
      existing.push(customer.id);
    } else {
      phraseToCustomerIds.set(phrase, [customer.id]);
    }
  }
  const ambiguous = new Set<string>();
  for (const ids of phraseToCustomerIds.values()) {
    if (ids.length > 1) {
      for (const id of ids) ambiguous.add(id);
    }
  }
  return ambiguous;
}

/**
 * Assign a confidence bucket to a single scored file based on its top
 * suggestions and the set of ambiguous customer ids.
 *
 * Rules (from spec):
 *   - bucket 1: top.score >= 0.90 AND top.score - second.score >= 0.12
 *   - bucket 2: top.score >= 0.72 AND top.score - second.score >= 0.05
 *   - bucket 3: everything else, including:
 *       - top customer is in the ambiguous set (normalized name collision)
 *       - margin between top and second is below the threshold for the
 *         bucket the top score otherwise qualifies for
 *       - no suggestions at all
 */
function bucketForFile(
  suggestions: Suggestion[],
  ambiguousCustomerIds: Set<string>,
): ConfidenceBucket {
  if (suggestions.length === 0) return 3;
  const top = suggestions[0];
  if (ambiguousCustomerIds.has(top.customer_id)) return 3;
  const secondScore = suggestions.length > 1 ? suggestions[1].score : 0;
  const margin = top.score - secondScore;
  if (top.score >= BUCKET_1_MIN_SCORE && margin >= BUCKET_1_MIN_MARGIN) {
    return 1;
  }
  if (top.score >= BUCKET_2_MIN_SCORE && margin >= BUCKET_2_MIN_MARGIN) {
    return 2;
  }
  return 3;
}

/**
 * Build suggested groups and the ungrouped list from per-file scoring.
 *
 * - Files in bucket 1 or 2 are grouped by their top customer id. Each
 *   resulting group's `initial_bucket` is the weakest (max) bucket among
 *   its members — `1` only if every member is bucket 1.
 * - Files in bucket 3 go to `ungrouped_file_ids`.
 * - Suggested groups have `source: "suggested"`, `state: "suggested"`,
 *   and their `selected_customer_id` set to the shared top customer id.
 *
 * The input order of `scored_files` is preserved for files inside each
 * group's `file_ids` and inside `ungrouped_file_ids`.
 */
export function buildGroupsFromMatches(args: {
  scored_files: ScoredFileInput[];
  customers: CustomerMinimal[];
}): BuildGroupsResult {
  const ambiguousCustomerIds = buildAmbiguousCustomerIds(args.customers);

  const fileBuckets: FileBucketAssignment[] = [];
  const ungroupedFileIds: string[] = [];

  // Group bucket 1/2 files by their top customer id, preserving input order.
  const groupedByCustomer = new Map<
    string,
    { file_ids: string[]; buckets: ConfidenceBucket[] }
  >();

  for (const scored of args.scored_files) {
    const bucket = bucketForFile(scored.suggestions, ambiguousCustomerIds);
    fileBuckets.push({ id: scored.id, initial_bucket: bucket });

    if (bucket === 3) {
      ungroupedFileIds.push(scored.id);
      continue;
    }
    // bucket 1 or 2 — group by top customer id
    const topCustomerId = scored.suggestions[0].customer_id;
    const existing = groupedByCustomer.get(topCustomerId);
    if (existing) {
      existing.file_ids.push(scored.id);
      existing.buckets.push(bucket);
    } else {
      groupedByCustomer.set(topCustomerId, {
        file_ids: [scored.id],
        buckets: [bucket],
      });
    }
  }

  const groups: Group[] = [];
  for (const [customerId, entry] of groupedByCustomer.entries()) {
    // Weakest member bucket = max() because 1 = strongest, 3 = weakest.
    // Only buckets 1 and 2 can reach this branch.
    const weakestBucket: 1 | 2 = entry.buckets.includes(2) ? 2 : 1;
    groups.push({
      id: uuidV4(),
      file_ids: entry.file_ids,
      selected_customer_id: customerId,
      state: "suggested",
      source: "suggested",
      initial_bucket: weakestBucket,
    });
  }

  return {
    file_buckets: fileBuckets,
    groups,
    ungrouped_file_ids: ungroupedFileIds,
  };
}
